import React, { useCallback, useState } from "react";

import { Axis, Chart, Legend, LineAdvance, Tooltip } from "bizcharts";
import { findLast } from "lodash";
import useNotification from "../../hooks/useNotification";
import useDateFnsLocale from "../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../hooks/useFormatDate";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import { getClosestGrade } from "../../utils/reviewUtils";
import { Datum } from "../reviews/UserFollowUpAnalytics";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText
} from '@chakra-ui/react';

import GradeUserSkillModal from "./GradeUserSkillModal";
import useConfirm from "../utils/confirm/useConfirm";
import { useDeleteAnswers, UserMinimalFragment } from '../../graphql/generated/definitions';

type UserSkillSectionDetailModalProps = {
  datum?: Datum;
  open: boolean;
  user: UserMinimalFragment;
  answers;
  options: {
    value: number;
    label: string;
  }[];
  onClose: () => void;
  refetch;
};

const UserSkillSectionDetailModal: React.FC<UserSkillSectionDetailModalProps> =
  ({ datum, options, open, onClose, answers, user, refetch }) => {
    const t = useTranslations();
    const confirm = useConfirm();
    const dateFnsLocale = useDateFnsLocale();
    const [gradeSkillModal, setGradeSkillModal] = useState({
      open: false,
      question: null,
    });
    const viewer = useViewer();
    const { notifySuccess, notifyError } = useNotification();

    const [deleteAnswers] = useDeleteAnswers();

    const handleDeleteAnswers = useCallback(
      async (digest) => {
        try {
          await deleteAnswers({
            variables: {
              input: {
                userId: viewer.id,
                collaboratorId: user.id,
                questionDigest: digest,
              },
            },
          });
          notifySuccess({
            title: t(
              "users.skills.notification.success.delete.title"
            ) as string,
            description: t(
              "users.skills.notification.success.delete.body"
            ) as string,
          });
        } catch {
          notifyError({
            title: t("users.skills.notification.error.title") as string,
          });
        }
        onClose();
        refetch();
      },
      [deleteAnswers, viewer, user]
    );

    const formatter = useCallback(
      (v: string | number | Date) =>
        dateFormatter(
          // Bizcharts internally converts Date to timestamp string
          typeof v === "string" ? parseInt(v) : v,
          t(`dateFormat.short`) as string,
          t("dateFormat.fallback") as string,
          dateFnsLocale
        ),
      [dateFnsLocale]
    );

    if (!open || !datum) return null;

    return (
      <>
        <Modal
          isOpen={open}
          onClose={onClose}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {datum.metadata.title}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack
                align="center"
              >
                <Stat>
                  <StatLabel>
                    {t("reviews.currentLevel")}
                  </StatLabel>
                  <StatNumber>
                    {
                      getClosestGrade(options, findLast(datum.data).grade).label
                    }
                  </StatNumber>
                  {
                    datum.metadata.objective && (
                      <StatHelpText>
                        {t("reviews.targetLevel", {
                          level: getClosestGrade(
                            options,
                            datum.metadata.objective
                          ).label,
                        })}
                      </StatHelpText>
                    )
                  }
                </Stat>

                <Spacer />

                <Chart
                  autoFit
                  height={400}
                  data={datum.data}
                  padding="auto"
                  scale={{
                    grade: {
                      range: [0, 1],
                      ticks: options.map((option) => option.value),
                    },
                  }}
                >
                  <LineAdvance
                    position="date*grade"
                    color="user"
                    shape="smooth"
                    tooltip={[
                      "user*grade",
                      (name, v) => {
                        return {
                          value: getClosestGrade(options, v).label,
                          name,
                        };
                      },
                    ]}
                    point
                  />

                  <Axis
                    name="date"
                    label={{
                      formatter,
                    }}
                  />
                  <Axis
                    name="grade"
                    position="left"
                    label={{
                      formatter: (v) =>
                        getClosestGrade(options, parseFloat(v)).label,
                    }}
                  />
                  <Legend
                    name="user"
                    position="bottom"
                    offsetY={20}
                    filter={(v) =>
                      v !==
                      (t(
                        "statistics.subSections.referentSkillRatingAverage"
                      ) as string)
                    }
                  />
                  <Tooltip
                    shared
                    showMarkers
                    showCrosshairs
                    title={(
                      _,
                      datum: {
                        date: Date;
                      }
                    ) => formatter(datum.date)}
                  />
                </Chart>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack>
                {answers[datum.metadata.title].find((a) => a.canDestroy) && (
                  <>
                    <Button
                      id="deleteGrades"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => {
                        // FIXME: maybe move everything in a handler?
                        confirm({
                          title: t("users.skills.deleteGrades"),
                          message: t("users.skills.confirmDeleteGrades"),
                          buttons: [
                            { text: t("forms.actions.cancel") },
                            {
                              colorScheme: "red",
                              id: "delete-grades",
                              text: t("forms.actions.delete"),
                              onClick: async () =>
                                handleDeleteAnswers(datum.metadata.question.digest),
                            },
                          ],
                        });
                      }}
                    >
                      {t("users.skills.deleteGrades")}
                    </Button>
                    <Spacer />
                  </>
                )}
                {viewer.isCapableOfCreatingStandaloneAnswers && (
                  <Button
                    id="grade"
                    onClick={() => {
                      setGradeSkillModal({
                        open: true,
                        question: datum.metadata.question,
                      });
                    }}
                    colorScheme="blue"
                  >
                    {t("users.skills.grade")}
                  </Button>
                )}
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {gradeSkillModal.question && (
          <GradeUserSkillModal
            refetch={refetch}
            open={gradeSkillModal.open && !!gradeSkillModal.question}
            question={gradeSkillModal.question}
            onClose={() => setGradeSkillModal({ open: false, question: null })}
            userId={user.id}
          />
        )}
      </>
    );
  };

  export default React.memo(UserSkillSectionDetailModal);